import React from 'react';
import { Seat } from './types';
import { isSelectable } from './rules';
import { SEAT_COLORS, SEAT_HOVER_COLORS } from './constants';

interface SeatComponentProps {
  seat: Seat;
  isSelected: boolean;
  onSelect: (seat: Seat) => void;
}

export const SeatComponent = React.memo(({ 
  seat, 
  isSelected, 
  onSelect
}: SeatComponentProps) => {
  const selectable = isSelectable(seat);
  const [isHovering, setIsHovering] = React.useState(false);

  const baseStyles = React.useMemo(() => {
    if (isSelected) {
      return SEAT_COLORS.selected;
    }
    return SEAT_COLORS[seat.status];
  }, [seat.status, isSelected]);

  const hoverStyles = React.useMemo(() => {
    if (!selectable) return {} as { filter?: string };
    return isSelected ? SEAT_HOVER_COLORS.selected : SEAT_HOVER_COLORS.available;
  }, [selectable, isSelected]);

  const handleMouseEnter = () => {
    if (selectable) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    if (selectable) {
      setIsHovering(false);
    }
  };

  const handleClick = () => {
    if (selectable) {
      onSelect(seat);
    }
  };

  const currentStyles = React.useMemo(() => {
    if (isHovering && selectable) {
      return { ...baseStyles, ...hoverStyles };
    }
    return baseStyles;
  }, [baseStyles, hoverStyles, isHovering, selectable]);

  // Extract filter separately since it's optional
  const filterValue = React.useMemo(() => {
    if (isHovering && selectable && 'filter' in hoverStyles) {
      return hoverStyles.filter;
    }
    return undefined;
  }, [isHovering, selectable, hoverStyles]);

  return (
    <g>
      <rect
        x={seat.x}
        y={seat.y}
        width={seat.width}
        height={seat.height}
        fill={currentStyles.fill}
        stroke={currentStyles.stroke}
        strokeWidth={currentStyles.strokeWidth}
        opacity={currentStyles.opacity}
        style={{
          transition: 'all 0.2s ease',
          cursor: selectable ? 'pointer' : 'not-allowed',
          filter: filterValue,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onTouchStart={handleMouseEnter} // Touch support
        onTouchEnd={handleMouseLeave}
        aria-label={`Seat ${seat.id}, ${seat.status}, ${seat.sectionId}`}
        role="button"
        tabIndex={selectable ? 0 : -1}
        onKeyDown={(e) => {
          if (selectable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <title>
          {`Seat: ${seat.id}\nSection: ${seat.sectionId}\nStatus: ${seat.status}${isSelected ? '\n✓ Selected' : ''}`}
        </title>
      </rect>
      {/* Invisible larger touch target for mobile */}
      <rect
        x={seat.x - 5}
        y={seat.y - 5}
        width={seat.width + 10}
        height={seat.height + 10}
        fill="transparent"
        className="md:hidden" // Only on mobile
        style={{
          cursor: selectable ? 'pointer' : 'not-allowed',
        }}
        onClick={handleClick}
        onTouchStart={handleMouseEnter}
        onTouchEnd={handleMouseLeave}
      />
      {seat.code && (
        <text
          x={seat.x + seat.width / 2}
          y={seat.y + seat.height / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="8"
          fontWeight="500"
          fill="#333"
          className="pointer-events-none select-none"
        >
          {seat.code}
        </text>
      )}
    </g>
  );
});

SeatComponent.displayName = 'SeatComponent';

